import * as $ from 'jquery';
import 'jquery-colorbox';
import 'jquery-zoom';
import DialogUtils from '../../lib/DialogUtils';

declare global {
	interface JQuery {
		zoom(options?: any): JQuery;
	}
}

/**
 * A very simple image gallery.
 * (Note: Before anything like this should be added to NPM the hardcoded configuration needs to be
 *  removed, more hooks are needed to customize galleries, more method (e.g. gallery.next()), etc.
 *  Additionally this should be expanded to be used for 'carousels' too and we should surely first
 *  look for a good existing library to use instead of making one.)
 */
export default class Gallery {
	constructor() {
		const selector_gallery = '.gallery';
		const selector_media   = '.gallery__media';
		const selector_thumbs  = '.gallery__thumb';
		const selector_viewer  = '.gallery__view';

		let $gallery = $(selector_gallery);
		if ($gallery.length === 0) {
			return;
		}

		$gallery.each((index, element) => {
			let currentIndex = 0;
			let removeOldMediaTimeoutID: number = undefined;
			const $elem = $(element);
			const $galleryView = $elem.find(selector_viewer);
			const $galleryThumbs = $elem.find(selector_thumbs);

			function removeOldMedia(): void {
				// Remove all but first one
				let $mediaElements = $galleryView.find(selector_media);
				for (let i = 1; i < $mediaElements.length; ++i) {
					$($mediaElements[i]).remove();
				}
				$galleryView.attr('data-status', '');

				removeOldMediaTimeoutID = undefined;
			}

			function activateByIndex(newIndex: number): void {
				//*DEBUG*/console.log('activateByIndex', newIndex);
				// updates global
				currentIndex = newIndex;

				const $thumb  = $($galleryThumbs[newIndex]);
				const $image  = $thumb.find('img');
				const isVideo = $thumb.attr('data-video');
				const src     = $thumb.attr('data-view');
				const href    = $thumb.attr('href') || '';
				const alt     = $image.attr('alt') || '';

				const $galleryMedia = $galleryView.find(selector_media);

				if ($galleryMedia.attr('src') !== src) {
					if (removeOldMediaTimeoutID !== undefined) {
						clearTimeout(removeOldMediaTimeoutID);
					}

					// Remove 'active' class from all thumbnails and then add to current
					$galleryThumbs.attr('aria-current', 'false').filter($thumb).attr('aria-current', 'true');

					// Load the new media
					const newImageParts = [];
					newImageParts.push(`<div class="gallery__media">`);
					newImageParts.push(`	<a class="gallery__image" href="${ href }" aria-label="${ alt }, click to view full size in new window" target="_blank" data-video="${isVideo}">`);
					newImageParts.push(`		<img src="${ src }" alt="${ alt }" />`);
					newImageParts.push(`	</a>`);
					newImageParts.push(`</div>`);

					$galleryView.attr('data-status', 'loading');
					$galleryView.find(selector_media).before($(newImageParts.join('')));
					$galleryView.find(selector_media).zoom({ url: $thumb.attr('href') });

					// Removing removed once done
					removeOldMediaTimeoutID = window.setTimeout(removeOldMedia, 1000);
				}
			}

			function buildControls(container: HTMLElement): void {
				//*DEBUG*/console.log('buildControls');

				if ($galleryThumbs.length <= 1) return;

				const prevButton : Element = document.createElement('button');
				prevButton.setAttribute('class', 'control control--prev');
				prevButton.setAttribute('aria-label', 'Previous Item');
				prevButton.innerHTML = `
					<svg viewBox="0 0 24 32" aria-hidden="true">
						<path d="M20.911 5.661l-9.482 9.482 9.482 9.482c0.446 0.446 0.446 1.161 0 1.607l-2.964 2.964c-0.446 0.446-1.161 0.446-1.607 0l-13.25-13.25c-0.446-0.446-0.446-1.161 0-1.607l13.25-13.25c0.446-0.446 1.161-0.446 1.607 0l2.964 2.964c0.446 0.446 0.446 1.161 0 1.607z"></path>
					</svg>
					<span>Prev</span>`;
				prevButton.addEventListener('click', (event: MouseEvent) => {
					const newIndex = currentIndex ? currentIndex - 1 : $galleryThumbs.length - 1;
					activateByIndex(newIndex);
				});
				container.prepend(prevButton);

				const nextButton : Element = document.createElement('button');
				nextButton.setAttribute('class', 'control control--next');
				nextButton.setAttribute('aria-label', 'Next Item');
				nextButton.innerHTML = `
					<span>Next</span>
					<svg viewBox="0 0 22 32" aria-hidden="true">
						<path d="M19.768 15.946l-13.25 13.25c-0.446 0.446-1.161 0.446-1.607 0l-2.964-2.964c-0.446-0.446-0.446-1.161 0-1.607l9.482-9.482-9.482-9.482c-0.446-0.446-0.446-1.161 0-1.607l2.964-2.964c0.446-0.446 1.161-0.446 1.607 0l13.25 13.25c0.446 0.446 0.446 1.161 0 1.607z"></path>
					</svg>`;
				nextButton.addEventListener('click', (event: MouseEvent) => {
					const newIndex = currentIndex < $galleryThumbs.length - 1 ? currentIndex + 1 : 0;
					activateByIndex(newIndex);
				});
				container.append(nextButton);
			}

			function handleThumbClick(event: JQuery.ClickEvent): void {
				//*DEBUG*/console.log('handleThumbClick', event.currentTarget, this);
				event.preventDefault();

				const newIndex = $(this).parent().index();
				activateByIndex(newIndex);

				if (event.currentTarget.dataset.video == "true"){
					$(event.currentTarget).colorbox({
						transition: 'elastic',
						opacity: .60,
						initialWidth: 500,
						initialHeight: 300,
						width: 1280,
						height: 720,
						maxWidth: '95%',
						maxHeight: '95%',
						fixed: true,
						close: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 44 44" height="44" fill="#FFFFFF"><path d="M13.458 15.336l1.485-1.485 15.344 15.344-1.485 1.485z"> </path><path d="M12.961 29.2l15.344-15.343 1.485 1.485-15.344 15.344z"> </path></svg><span>Close</span>',
						iframe: true,
						onOpen: () => {
							document.querySelector('html').classList.add('lockScroll');
							document.getElementById('colorbox').setAttribute('aria-label', event.currentTarget.getAttribute('aria-description') || 'A Video');
						},
						onClosed: () => {
							document.querySelector('html').classList.remove('lockScroll');
							document.getElementById('colorbox').setAttribute('aria-label', '');
						},
						onComplete: () => {
						}
					});
				}
			}

			buildControls($galleryView.get(0));
			$galleryThumbs.on('click', handleThumbClick);
			activateByIndex(0);
		});

	}
}
